import { z } from 'zod';
import { somarPercentual } from './aritmetica.ts';
import {
  acaoSaldoTituloSchema,
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import {
  MAXIMO_RATEIOS_TITULO,
  MENSAGEM_PARCELA_MAIOR_QUE_TOTAL,
  marcarRepetidos,
  numeroDaParcelaSchema,
  numeroDocumentoSchema,
  parcelaCabeNoTotal,
  rateioDeObraCabe,
  rateioDoTituloEntradaSchema,
  rateioDoTituloSchema,
  situacaoTituloSchema,
  valorAcessorioSchema,
  valorLiquidoDaBaixa,
  type SituacaoTitulo,
} from './titulos.ts';

/**
 * Contas a receber — o título e a baixa dele (`.spec/010`, módulo Recebimento;
 * `.spec/016`, seção 10: "`contas_receber` é análogo, sem as colunas de origem e
 * com `cliente_id`").
 *
 * ## Duas portas de entrada, e uma delas é nova
 *
 * Este comentário dizia "uma porta de entrada, não duas", e prometia que o par
 * `origem`/`editavel` entraria "quando o contrato de recebimento passar a gerar
 * parcelas". Passou. O arquivo cumpre a promessa, e com uma diferença que
 * continua separando os dois módulos:
 *
 * - **Três origens, não quatro.** `CONTRATO`, `OBRA` e `MANUAL`. Não há nota de
 *   compra nem mão de obra deste lado, e por isso a tabela tem uma coluna de
 *   origem só e nenhum `CHECK` de exclusividade — uma coluna sozinha não colide
 *   com nada. `OBRA` entrou em 18/09/2026, quando a obra passou a faturar por
 *   conta própria (`.spec/015`, decisão 55); este comentário dizia "duas
 *   origens, não quatro" até ali.
 * - **`editavel` deixou de ser sempre verdadeiro.** O título gerado pelo
 *   contrato não se corrige aqui: quem o desfaz é a edição do contrato, que
 *   apaga os títulos e reabre o botão de gerar.
 * - **O contrato entra no payload, e não faz do título um derivado.** É o
 *   aditivo: depois que o financeiro do contrato foi movimentado, as parcelas
 *   não se refazem, e o que sobra é lançar aqui o valor a mais — vinculado
 *   àquele contrato, rateado nas obras dele. O título continua `MANUAL`, e por
 *   isso continua se editando e se excluindo por esta tela.
 * - **Continua sem reprogramação em rota separada.** Ela existe no pagar porque
 *   é o único caminho de escrita do título derivado de nota, que não tem outro
 *   documento a corrigir. O título de contrato tem: o contrato.
 *
 * ## Classificação contábil: uma coluna, e não rateio
 *
 * O contas a pagar divergiu da `.spec/016` e trocou `conta_contabil_id` por uma
 * tabela de índices. O motivo era a nota: uma parcela cobre itens de naturezas
 * diferentes — cimento, furadeira e serviço de guindaste no mesmo documento — e
 * uma coluna só jogaria a nota inteira na conta da primeira linha.
 *
 * **Esse motivo não existe aqui.** O título a receber é digitado, e quem o
 * digita sabe de que receita ele é; obrigá-lo a montar um rateio de 100% para
 * uma natureza só seria trabalho manual criado por simetria. A `.spec/010` pede
 * uma coluna, e é uma coluna.
 *
 * ## Rateio de obra: existe, e é opcional
 *
 * Aqui, sim, o receber acompanha o pagar. Obra é centro de custo do projeto, e
 * "quanto a Residencial Aurora faturou" é pergunta que só o rateio responde. A
 * soma pode ser menor que 100%: receita administrativa não pertence a obra
 * nenhuma, e escalar as fatias até fechar 100% atribuiria às obras um
 * faturamento que não foi delas.
 */

/* -------------------------------------------------------------------------- */
/* Origem e situação                                                          */
/* -------------------------------------------------------------------------- */

/**
 * De onde o título a receber veio.
 *
 * **Quatro**, e não as mesmas quatro do pagar: não há nota de compra nem mão
 * de obra deste lado. Os três documentos que geram crédito são o contrato de
 * recebimento, a **obra** e a **nota fiscal de serviço** — que entrou em
 * 06/10/2026 (`.spec/015`, decisão 90), quando este comentário ainda dizia
 * "três".
 *
 * É **gravada** em `contas_receber.origem`. Foi derivada de `contrato_id`
 * enquanto a geração era o único jeito de a chave estar preenchida; o aditivo
 * digitado — título `MANUAL` apontando para um contrato — desfez essa
 * equivalência. Hoje a chave responde *de qual contrato o título é* e a coluna
 * responde *quem o escreveu*, que são perguntas diferentes desde que existe
 * aditivo.
 *
 * `OBRA` nasceu já com essa distinção resolvida, e é por isso que ela tem chave
 * própria (`obra_id`) em vez de se apoiar no rateio: `contas_receber_rateio`
 * diz *de qual obra é a receita*, e um título digitado à mão rateado numa obra
 * responde "desta" sem ter saído da geração dela.
 *
 * A lista é própria, e não a do contas a pagar filtrada: oferecer `NOTA_COMPRA`
 * no filtro do recebimento seria oferecer um recorte que nunca traz linha.
 */
export const ORIGENS_TITULO_RECEBER = ['CONTRATO', 'OBRA', 'NOTA_SERVICO', 'MANUAL'] as const;
export const origemTituloReceberSchema = z.enum(ORIGENS_TITULO_RECEBER);
export type OrigemTituloReceber = z.infer<typeof origemTituloReceberSchema>;

export const ROTULO_ORIGEM_TITULO_RECEBER: Readonly<Record<OrigemTituloReceber, string>> = {
  CONTRATO: 'Contrato de recebimento',
  OBRA: 'Faturamento da obra',
  NOTA_SERVICO: 'Nota fiscal de serviço',
  MANUAL: 'Lançamento manual',
};

/**
 * Rótulos da situação. O enum e o cálculo são compartilhados (`titulos.ts`).
 *
 * `QUITADO` se lê **"Recebido"** deste lado. O código é o mesmo de propósito —
 * a regra "saldo zero fecha o título" é uma só —, mas ninguém diz que um cliente
 * "quitou" o boleto que a empresa emitiu.
 */
export const ROTULO_SITUACAO_TITULO_RECEBER: Readonly<Record<SituacaoTitulo, string>> = {
  EM_ABERTO: 'Em aberto',
  PARCIAL: 'Recebido em parte',
  QUITADO: 'Recebido',
};

/* -------------------------------------------------------------------------- */
/* Título                                                                     */
/* -------------------------------------------------------------------------- */

const valorDoTituloSchema = dinheiroSchema.refine(
  (v) => Number(v) > 0,
  'O valor do título deve ser maior que zero.',
);

const rateiosSchema = z
  .array(rateioDoTituloEntradaSchema)
  .max(MAXIMO_RATEIOS_TITULO, `O título tem no máximo ${MAXIMO_RATEIOS_TITULO} rateios.`)
  .prefault([]);

/**
 * Payload do título **digitado**.
 *
 * O que ele deliberadamente **não** tem, e por quê:
 *
 * - `valorSaldo` — nasce igual ao valor e depois só a baixa o move. Aceitá-lo
 *   deixaria um título se declarar meio recebido sem que nenhuma baixa
 *   existisse, e a conta corrente não teria a contrapartida do dinheiro que
 *   teria entrado.
 * - `origem` — não se digita, e é o que separa este payload da geração. Todo
 *   título que entra por esta porta nasce `MANUAL`, **inclusive o que aponta
 *   para um contrato**. Aceitá-la deixaria a tela criar uma parcela que o
 *   contrato conta como sua, e que a primeira edição dele apagaria.
 * - `situacao` — é leitura do saldo (`situacaoDoTitulo`, em `titulos.ts`).
 */
export const criarContaReceberSchema = z
  .object({
    /** Empresa do grupo que tem a receber — `pessoas` com `is_empresa`. */
    empresaId: uuidSchema,
    /** Dia em que o título entra no sistema. Data de negócio, sem hora. */
    dataLancamento: dataDoFatoSchema,
    /** De quem se tem a receber — `pessoas` com `is_cliente`. */
    clienteId: uuidSchema,
    /**
     * O contrato de recebimento a que o título pertence. **Opcional.**
     *
     * Este campo já esteve fora do payload de propósito, com o argumento de que
     * "origem não se digita". O argumento continua válido — e por isso `origem`
     * não entrou —, mas a chave não é a origem: ela é o **aditivo**.
     *
     * O caso é o do contrato cujo financeiro já foi movimentado. As parcelas não
     * se refazem enquanto houver baixa, e o valor a mais — aditivo de escopo,
     * reajuste, serviço extra pedido pelo cliente — precisa entrar em algum
     * lugar. Entra aqui, apontando para o contrato de onde vem, e o rateio diz
     * em quais obras dele a receita cai.
     *
     * O que o servidor confere: o contrato existe, é de **recebimento** e está
     * ativo. O que ele **não** exige é que a obra do rateio seja do contrato —
     * aditivo de obra que ainda não foi vinculada é caso legítimo, e recusá-lo
     * mandaria o usuário corrigir o cadastro do contrato para lançar um título.
     */
    contratoId: uuidSchema.nullable().prefault(null),
    /**
     * O documento que originou o crédito — nota de serviço, boleto, contrato.
     *
     * Obrigatório, e sempre digitado: nenhum documento gera título a receber
     * ainda. É o número que o cliente cita ao ligar perguntando do que se trata
     * a cobrança, e por onde a conciliação com o extrato acontece.
     */
    numeroDocumento: numeroDocumentoSchema,
    /** Natureza da receita. Só conta folha do plano recebe classificação. */
    contaContabilId: uuidSchema,
    /**
     * A parcela do documento e quantas ele tem — o "2/12" da tela.
     *
     * Aceita-se no digitado porque a venda parcelada é o caso comum deste lado,
     * e o operador lança um título por parcela: sem os campos, as doze linhas
     * ficariam indistinguíveis a não ser pelo vencimento — e é justamente o
     * "2/12" que o cliente cita ao ligar sobre o boleto. O padrão `1` faz o
     * título avulso nascer `1/1`.
     *
     * No título gerado pelo contrato quem os preenche é a geração, e este
     * payload não o alcança.
     */
    parcela: numeroDaParcelaSchema,
    totalParcelas: numeroDaParcelaSchema,
    dataVencimento: dataSchema,
    /**
     * Quando se espera receber. Nulo recebe o vencimento (`.spec/010`) — a cópia
     * é feita por quem grava, e não por `coalesce` na consulta: o fluxo de caixa
     * projetado lê esta data, e a primeira consulta escrita sem o `coalesce`
     * esconderia o título do relatório.
     */
    dataPrevisaoRecebimento: dataSchema.nullable().prefault(null),
    valor: valorDoTituloSchema,
    observacao: z.string().trim().max(500).prefault(''),
    rateio: rateiosSchema,
  })
  .superRefine((dados, ctx) => {
    if (dados.dataPrevisaoRecebimento && dados.dataPrevisaoRecebimento < dados.dataLancamento) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataPrevisaoRecebimento'],
        message: 'A previsão de recebimento não pode ser anterior ao lançamento do título.',
      });
    }

    if (!parcelaCabeNoTotal(dados)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parcela'],
        message: MENSAGEM_PARCELA_MAIOR_QUE_TOTAL,
      });
    }

    if (!rateioDeObraCabe(dados.rateio)) {
      const soma = somarPercentual(dados.rateio.map((r) => r.indiceRateio));
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateio'],
        message: `O rateio de obra soma ${soma}% e não pode passar de 100%. A parte que sobra fica sem obra, como receita administrativa.`,
      });
    }

    // A mesma obra duas vezes é digitação repetida — e o índice único da tabela
    // recusaria a segunda linha. Pegar aqui devolve o erro preso à linha errada,
    // e não uma violação de constraint que a tela não sabe onde mostrar.
    marcarRepetidos(
      dados.rateio.map((r) => `${r.obraId}|${r.obraParteId ?? ''}`),
      'rateio',
      'obraId',
      'Esta obra já está no rateio. Some os percentuais numa fatia só.',
      ctx,
    );
  });

export type CriarContaReceber = z.infer<typeof criarContaReceberSchema>;
export const atualizarContaReceberSchema = criarContaReceberSchema;
export type AtualizarContaReceber = z.infer<typeof atualizarContaReceberSchema>;

/** Título como a API o devolve, com os vínculos resolvidos e o saldo. */
export const contaReceberSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  clienteId: uuidSchema,
  clienteNome: z.string().nullable(),
  origem: origemTituloReceberSchema,
  /**
   * O contrato do título, gerado ou aditivo. Nulo no título avulso.
   *
   * Não implica origem `CONTRATO`: o aditivo é `MANUAL` e tem contrato. Ver
   * `ORIGENS_TITULO_RECEBER`.
   */
  contratoId: uuidSchema.nullable(),
  /**
   * Número do contrato de origem — o que a tela mostra para dizer "este título
   * veio daquele contrato". É o número por extenso, com os 60 caracteres que o
   * cadastro admite, e não o `numeroDocumento` do título, que tem 30.
   */
  contratoNumero: z.string().nullable(),
  /**
   * A obra que faturou este título, quando ele saiu da geração dela.
   *
   * **Não é o rateio.** O rateio pode apontar para a mesma obra num título
   * digitado à mão, e responde outra pergunta: de qual obra é a receita. Esta
   * chave responde quem escreveu o título, e é ela que a obra conta para saber
   * se já faturou. Nula em todo título que não veio da geração de uma obra.
   */
  obraId: uuidSchema.nullable(),
  /** Nome da obra de origem, desnormalizado — a tela mostra o nome, não o id. */
  obraNome: z.string().nullable(),
  /**
   * A nota fiscal de serviço que gerou o título (`.spec/015`, decisão 90).
   *
   * Terceira chave de origem, ao lado de `contratoId` e `obraId`, e pela mesma
   * razão da obra: a nota precisa saber quais títulos são **dela** para apagá-los
   * ao ser cancelada. A obra da nota vai no **rateio**, e não em `obraId` — a
   * obra não escreveu este título, e contá-lo como dela fecharia o botão de
   * gerar do cadastro da obra.
   */
  notaServicoId: uuidSchema.nullable(),
  /** O número da nota no sistema, para a tela dizer de qual nota o título veio. */
  notaServicoNumero: z.number().int().nullable(),
  /** `true` quando o título aceita edição de valor e vínculos — só o digitado. */
  editavel: z.boolean(),
  /** O documento que originou o crédito. Ver `numeroDocumentoSchema`. */
  numeroDocumento: z.string(),
  contaContabilId: uuidSchema,
  contaContabilCodigo: z.string().nullable(),
  contaContabilDescricao: z.string().nullable(),
  /**
   * A parcela e o total, separados — a tela é que os junta em "2/12".
   *
   * Devolver o texto pronto pouparia uma linha na tela e custaria a ordenação e
   * o filtro, que precisam do número.
   */
  parcela: z.number().int(),
  totalParcelas: z.number().int(),
  dataLancamento: dataSchema,
  dataVencimento: dataSchema,
  dataPrevisaoRecebimento: dataSchema,
  valor: dinheiroSchema,
  valorSaldo: dinheiroSchema,
  /** Derivada do saldo — ver `situacaoDoTitulo`, em `titulos.ts`. */
  situacao: situacaoTituloSchema,
  observacao: z.string(),
  rateio: z.array(rateioDoTituloSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type ContaReceber = z.infer<typeof contaReceberSchema>;

/**
 * Listagem: a página **mais os totais do recorte inteiro**.
 *
 * Os totais não saem da soma da página — somariam 50 linhas de um filtro com
 * 900 — e por isso não podem ser calculados no cliente. "Quanto entra esta
 * semana" é a pergunta que abre a tela, e ela é sobre o filtro, não sobre o que
 * coube na primeira página.
 */
export const listaContasReceberSchema = respostaListaSchema(contaReceberSchema).extend({
  totalValor: dinheiroSchema,
  totalSaldo: dinheiroSchema,
});

export type ListaContasReceber = z.infer<typeof listaContasReceberSchema>;

/**
 * Filtros.
 *
 * Sem `ativo`: título é documento de movimento, não cadastro. O recorte natural
 * é **cliente e vencimento** — é assim que se procura um recebimento, e é o
 * índice que a tabela tem.
 */
export const listarContasReceberQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  clienteId: uuidSchema.optional(),
  origem: origemTituloReceberSchema.optional(),
  /** "Quais títulos este contrato gerou" — o caminho de volta a partir dele. */
  contratoId: uuidSchema.optional(),
  /** Recorta pelas fatias do rateio: "quanto a Residencial Aurora tem a receber". */
  obraId: uuidSchema.optional(),
  contaContabilId: uuidSchema.optional(),
  /**
   * Situação, **como recorte e não como rótulo**.
   *
   * `EM_ABERTO` aqui traz tudo com `valor_saldo > 0`, incluindo o parcialmente
   * recebido — quem pergunta o que ainda se tem a receber inclui o título com
   * recebimento parcial. `PARCIAL` isola só esses, e os dois se sobrepõem de
   * propósito. Ver `filtroDeSituacao`, no repositório.
   */
  situacao: situacaoTituloSchema.optional(),
  /** Vencimento — início e fim, inclusivos. */
  vencimentoDe: dataSchema.optional(),
  vencimentoAte: dataSchema.optional(),
  /** Previsão de recebimento — o recorte do fluxo de caixa projetado. */
  previsaoDe: dataSchema.optional(),
  previsaoAte: dataSchema.optional(),
});

export type ListarContasReceberQuery = z.infer<typeof listarContasReceberQuerySchema>;

export const COLUNAS_ORDENAVEIS_CONTA_RECEBER = [
  'dataVencimento',
  'dataPrevisaoRecebimento',
  'dataLancamento',
  'valor',
  'valorSaldo',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Baixa                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Payload da baixa — o recebimento.
 *
 * Idêntico em forma ao do contas a pagar, e diferente em sentido: aqui o
 * principal **entra** na conta corrente. O sinal não está neste arquivo; está em
 * `sentidoMovimento('RECEBER', …)`, em `@construtora/domain`, que é onde ele tem
 * teste unitário.
 *
 * O que o payload **não** tem:
 *
 * - `acaoSaldoTitulo` — é copiada do histórico financeiro escolhido, no momento
 *   da baixa (`.spec/016`, seção 10). Deixar o cliente mandá-la permitiria
 *   fechar um título com um histórico que aumenta saldo, e a cópia perderia o
 *   sentido de ser cópia.
 * - `valorLiquido` — é conta, não dado (ver `valorLiquidoDaBaixa`). Aceitá-lo
 *   deixaria o extrato bancário e o título discordarem por um número digitado.
 * - `contaReceberId` — vem da URL. No corpo, um id diferente do da rota daria
 *   duas respostas para "qual título estou baixando".
 */
export const criarBaixaContaReceberSchema = z
  .object({
    /** Dia em que o dinheiro entrou. Data de negócio, sem hora. */
    dataMovimentacao: dataDoFatoSchema,
    /** Conta corrente que recebe — o saldo dela anda nesta gravação. */
    contaCorrenteId: uuidSchema,
    /**
     * Forma de recebimento, e a **única escolha de histórico** da baixa.
     *
     * Dela saem duas coisas: a ação sobre o saldo do título e o histórico
     * bancário das movimentações. O bancário já é atributo da forma de
     * recebimento no cadastro — pedi-lo de novo aqui criava duas respostas para
     * a mesma pergunta, sem nada obrigando as duas a concordarem. A baixa é
     * recusada quando a forma escolhida não tem o vínculo.
     *
     * A conta contábil também saiu do payload: a movimentação principal usa a
     * **do título**, que aqui é uma só (`contas_receber.conta_contabil_id`). É a
     * diferença em relação ao contas a pagar, onde o título tem N classificações
     * e o principal é rateado entre elas.
     */
    historicoFinanceiroId: uuidSchema,
    /** O que a baixa abate do saldo do título. Move o principal na conta corrente. */
    valorBaixaSaldoTitulo: dinheiroSchema.refine(
      (v) => Number(v) > 0,
      'O valor da baixa deve ser maior que zero.',
    ),
    valorJuros: valorAcessorioSchema,
    valorOutros: valorAcessorioSchema,
    valorDesconto: valorAcessorioSchema,
    observacao: z.string().trim().max(500).prefault(''),
  })
  .superRefine((dados, ctx) => {
    // O líquido é o que entra no banco. Zero ou negativo significaria
    // recebimento que não movimenta ou que devolve dinheiro ao cliente — e
    // nenhum dos dois é uma baixa; o segundo é um estorno, que tem caminho
    // próprio.
    if (Number(valorLiquidoDaBaixa(dados)) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['valorDesconto'],
        message:
          'O desconto zera ou inverte o valor líquido da baixa. O líquido é o que entra no banco e precisa ser maior que zero.',
      });
    }
  });

export type CriarBaixaContaReceber = z.infer<typeof criarBaixaContaReceberSchema>;

export const baixaContaReceberSchema = z.object({
  id: uuidSchema,
  contaReceberId: uuidSchema,
  dataMovimentacao: dataSchema,
  contaCorrenteId: uuidSchema,
  contaCorrenteDescricao: z.string().nullable(),
  historicoFinanceiroId: uuidSchema,
  historicoFinanceiroDescricao: z.string().nullable(),
  /** Cópia congelada da ação do histórico no momento da baixa. */
  acaoSaldoTitulo: acaoSaldoTituloSchema,
  valorBaixaSaldoTitulo: dinheiroSchema,
  valorJuros: dinheiroSchema,
  valorOutros: dinheiroSchema,
  valorDesconto: dinheiroSchema,
  valorLiquido: dinheiroSchema,
  /** Nulo enquanto a baixa vale. Preenchido, ela consta como estornada. */
  estornadaEm: dataHoraSchema.nullable(),
  estornadaPorNome: z.string().nullable(),
  motivoEstorno: z.string().nullable(),
  observacao: z.string(),
  createdAt: dataHoraSchema,
});

export type BaixaContaReceber = z.infer<typeof baixaContaReceberSchema>;

export const listaBaixasContaReceberSchema = respostaListaSchema(baixaContaReceberSchema);
export type ListaBaixasContaReceber = z.infer<typeof listaBaixasContaReceberSchema>;

/** O rótulo é neutro e mora em `titulos.ts`; a dica fala de crédito e é daqui. */
export const DICA_ACAO_SALDO_TITULO_RECEBER: Readonly<
  Record<z.infer<typeof acaoSaldoTituloSchema>, string>
> = {
  DIMINUI_SALDO: 'O valor da baixa abate o que o cliente ainda deve.',
  AUMENTA_SALDO:
    'O valor da baixa soma ao que se tem a receber — é o caso de juros e multa lançados como baixa.',
  MANTEM_SALDO: 'O dinheiro entra na conta corrente, mas o saldo do título não muda.',
};
